import { useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { uploadFileToVps, deleteFileFromVps } from "@/lib/vpsStorage";
import Swal from "sweetalert2";

interface Props {
  bookId: string;
  publicSlug: string | null;
  qrOverlayLogoUrl: string | null;
  onLogoChange: (url: string | null) => void;
  tenantId?: string;
}

export default function QRTab({ bookId, publicSlug, qrOverlayLogoUrl, onLogoChange, tenantId }: Props) {
  const [logoInput,  setLogoInput]  = useState(qrOverlayLogoUrl ?? "");
  const [saving,     setSaving]     = useState(false);
  const [copied,     setCopied]     = useState(false);
  const [uploading,  setUploading]  = useState(false);
  const [pendingFileId, setPendingFileId] = useState<string | null>(null);

  const publicUrl = publicSlug
    ? `${window.location.origin}/guestbook/${publicSlug}`
    : null;

  const qrApiUrl = publicUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(publicUrl)}&size=300x300&margin=16&bgcolor=ffffff&color=1e293b&format=png`
    : null;

  const handleCopy = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWhatsApp = () => {
    if (!publicUrl) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(`¡Deja tu mensaje en el libro de firmas! ${publicUrl}`)}`, "_blank");
  };

  const handleDownloadQR = () => {
    if (!qrApiUrl) return;
    const a = document.createElement("a");
    a.href = qrApiUrl;
    a.download = `qr-guestbook-${publicSlug}.png`;
    a.target = "_blank";
    a.click();
  };

  const handleSaveLogo = async () => {
    setSaving(true);
    const { error } = await supabase
      .from("signature_books")
      .update({ qr_overlay_logo_url: logoInput.trim() || null, updated_at: new Date().toISOString() })
      .eq("id", bookId);
    setSaving(false);
    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error" });
    } else {
      onLogoChange(logoInput.trim() || null);
      Swal.fire({ title: "Logo guardado", icon: "success", timer: 1200, showConfirmButton: false });
    }
  };

  const handleLogoUpload = async (file: File) => {
    if (!tenantId) return;
    setUploading(true);
    setPendingFileId(null);
    try {
      const result = await uploadFileToVps({
        file,
        tenantId,
        entityType: "signature_book_qr_logo",
        entityId: bookId,
        folder: "signature_books",
        allowedTypes: ["image/png", "image/jpeg", "image/webp", "image/svg+xml"],
        maxSizeMB: 5,
      });
      if (result.success && result.url) {
        setPendingFileId(result.fileId ?? null);
        setLogoInput(result.url);
        const { error } = await supabase
          .from("signature_books")
          .update({ qr_overlay_logo_url: result.url, updated_at: new Date().toISOString() })
          .eq("id", bookId);
        if (!error) {
          onLogoChange(result.url);
          Swal.fire({ title: "Logo subido", icon: "success", timer: 1200, showConfirmButton: false });
        } else {
          // Cleanup: delete uploaded file if DB update failed
          if (result.fileId) {
            await deleteFileFromVps({ fileId: result.fileId, tenantId, silent: true });
          }
          Swal.fire({ title: "Error", text: error.message, icon: "error" });
        }
      } else {
        Swal.fire({ title: "Error", text: result.error || "Upload failed", icon: "error" });
      }
    } catch (err) {
      Swal.fire({ title: "Error", text: err instanceof Error ? err.message : "Upload failed", icon: "error" });
    } finally {
      setUploading(false);
      setPendingFileId(null);
    }
  };

  if (!publicSlug) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center bg-white rounded-2xl border border-dashed border-slate-200 max-w-sm">
        <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-slate-100 text-slate-400 mb-3">
          <i className="ri-qr-code-line text-2xl"></i>
        </div>
        <p className="text-slate-500 font-medium text-sm">Sin slug público</p>
        <p className="text-slate-400 text-xs mt-1">Guarda el libro para generar el QR</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-xl">
      {/* QR Card */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-slate-700 font-semibold text-sm mb-5 flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center text-amber-500"><i className="ri-qr-code-line"></i></span>
          Código QR del Libro
        </h3>

        <div className="flex flex-col sm:flex-row items-start gap-6">
          {/* QR image */}
          <div className="relative shrink-0">
            {qrApiUrl && (
              <div className="w-40 h-40 rounded-2xl overflow-hidden border border-slate-200 bg-white relative">
                <img src={qrApiUrl} alt="QR Code" className="w-full h-full object-contain" />
                {qrOverlayLogoUrl && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-10 h-10 bg-white rounded-lg overflow-hidden shadow border border-slate-200 flex items-center justify-center">
                      <img src={qrOverlayLogoUrl} alt="logo" className="w-full h-full object-contain" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex-1 space-y-3">
            {/* Link */}
            <div>
              <p className="text-xs text-slate-400 mb-1.5 font-medium">Link público</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-600 truncate">
                  {publicUrl}
                </code>
                <button
                  onClick={handleCopy}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer shrink-0 ${
                    copied ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                >
                  <i className={copied ? "ri-check-line text-sm" : "ri-clipboard-line text-sm"}></i>
                </button>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleDownloadQR}
                className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-900 text-white text-xs font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-2-line"></i>
                Descargar QR
              </button>
              <button
                onClick={handleWhatsApp}
                className="flex items-center gap-2 px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line"></i>
                WhatsApp
              </button>
              <button
                onClick={() => window.open(publicUrl!, "_blank")}
                className="flex items-center gap-2 px-3 py-2 border border-slate-200 text-slate-600 hover:text-slate-800 text-xs font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-external-link-line"></i>
                Ver libro
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay logo upload */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="text-slate-700 font-semibold text-sm mb-1 flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center text-amber-500"><i className="ri-image-line"></i></span>
          Logo sobre el QR (opcional)
        </h3>
        <p className="text-xs text-slate-400 mb-4">Agrega tu logo al centro del código QR para personalizar el diseño</p>

        {tenantId ? (
          <div className="space-y-3">
            {qrOverlayLogoUrl ? (
              <div className="relative rounded-xl overflow-hidden border border-slate-200 h-24 w-24 bg-white flex items-center justify-center">
                <img src={qrOverlayLogoUrl} alt="logo" className="w-full h-full object-contain p-2" />
                <button
                  onClick={() => { setLogoInput(""); onLogoChange(null); }}
                  className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-red-500 text-white rounded-full shadow cursor-pointer"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              </div>
            ) : (
              <div
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files?.[0];
                  if (file) handleLogoUpload(file);
                }}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => document.getElementById("qr-logo-upload")?.click()}
                className="border-2 border-dashed border-slate-200 rounded-xl p-5 text-center cursor-pointer hover:border-slate-300 hover:bg-slate-50 transition-all"
              >
                {uploading ? (
                  <div className="flex items-center gap-2 text-slate-500 text-sm justify-center">
                    <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                    Subiendo…
                  </div>
                ) : (
                  <>
                    <div className="w-8 h-8 mx-auto mb-1 flex items-center justify-center rounded-lg bg-slate-100 text-slate-400">
                      <i className="ri-upload-cloud-2-line text-lg"></i>
                    </div>
                    <p className="text-slate-500 text-xs font-medium">Click o arrastra logo</p>
                    <p className="text-slate-400 text-[10px]">PNG con fondo transparente ideal</p>
                  </>
                )}
                <input
                  id="qr-logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleLogoUpload(file);
                  }}
                  disabled={uploading}
                  className="hidden"
                />
              </div>
            )}
          </div>
        ) : (
          <div className="flex gap-2">
            <input
              type="url"
              value={logoInput}
              onChange={(e) => setLogoInput(e.target.value)}
              placeholder="https://tu-logo.com/logo.png"
              className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
            <button
              onClick={handleSaveLogo}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <i className="ri-save-line"></i>}
              Guardar
            </button>
          </div>
        )}
      </div>

      {/* Share instructions */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-100 p-5">
        <h3 className="text-amber-800 font-semibold text-sm mb-3">Cómo compartir el libro</h3>
        <div className="space-y-2.5">
          {[
            { icon: "ri-qr-code-line", text: "Imprime el QR y colócalo en las mesas del evento" },
            { icon: "ri-whatsapp-line", text: "Comparte el link por WhatsApp antes del evento" },
            { icon: "ri-smartphone-line", text: "Los invitados escanean el QR con su celular" },
            { icon: "ri-pencil-line", text: "Escriben su mensaje directamente sobre las fotos" },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-500/10 text-amber-600 shrink-0">
                <i className={`${item.icon} text-sm`}></i>
              </div>
              <p className="text-amber-700 text-xs">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}